import { InsuranceQueries } from '@frontend/api-insurance';
import { base64ToPdfBlob } from '../helpers';
import { usePersonInfoStore } from '../providers';
import { useGetLocationVerticalForPayerList } from './use-get-location-vertical-for-payer-list';

interface GetInsuranceEligibilityReportUrlParams {
  locationId: string;
  personId: string;
  sourceId: string;
  personPMSId: string;
}

const getInsuranceEligibilityReportUrl = ({
  locationId,
  personId,
  sourceId,
  personPMSId,
}: GetInsuranceEligibilityReportUrlParams) => {
  const baseUrl = window.location.origin;
  const userToken = window.localStorage.getItem('token');

  return `${baseUrl}/insurance-eligibility-report/?locationId=${locationId}&personId=${personId}&sourceId=${sourceId}&personPMSId=${personPMSId}&authToken=${userToken}`;
};

type UseOpenInsuranceReportParams = {
  requestId?: string;
};

export const useOpenInsuranceReport = ({ requestId = '' }: UseOpenInsuranceReportParams) => {
  const { personInfo, locationId } = usePersonInfoStore();
  const personId = personInfo?.PersonID as string;
  const sourceId = personInfo?.SourceID;
  const personPMSId = personInfo?.PMID;

  const { isDental, isWellnessVertical } = useGetLocationVerticalForPayerList(locationId || '');

  const {
    isLoading,
    isRefetching,
    refetch: getInsuranceEligibilityReportForNonDental,
  } = InsuranceQueries.useGetNonDentalInsuranceEligibilityReport(locationId || '', requestId, {
    enabled: false,
  });

  const handleOpenInsuranceReportForNonDental = (insuranceEligibilityReportForNonDental: string) => {
    if (!isLoading && insuranceEligibilityReportForNonDental) {
      const pdfBlob = base64ToPdfBlob(insuranceEligibilityReportForNonDental);
      const url = URL.createObjectURL(pdfBlob);

      // Open the URL in a new window
      window.open(url, 'noopener,noreferrer');

      // Clean up the URL object to prevent memory leaks
      URL.revokeObjectURL(url);
    }
  };

  const handleOpenInsuranceReportForDental = () => {
    const url = getInsuranceEligibilityReportUrl({
      personId,
      locationId: locationId ?? '',
      sourceId: sourceId ?? '',
      personPMSId: personPMSId ?? '',
    });
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleOpenInsuranceReport = () => {
    if (isDental) {
      handleOpenInsuranceReportForDental();
    } else if (isWellnessVertical) {
      getInsuranceEligibilityReportForNonDental().then((eligibilityReport) => {
        if (eligibilityReport?.data?.file) {
          handleOpenInsuranceReportForNonDental(eligibilityReport?.data?.file);
        }
      });
    }
  };

  return { handleOpenInsuranceReport, isLoading: isLoading || isRefetching };
};
